import lamejs from 'lamejs';

/**
 * Fallback clip extraction built on the Web Audio API + lamejs, plus the
 * capability probe used to decide whether it is needed.
 *
 * ffmpeg.wasm stays the primary encoder. Its core (@ffmpeg/core 0.12.x) is
 * compiled with WebAssembly SIMD instructions, so it cannot be instantiated in
 * environments that report "Wasm SIMD unsupported". Trimming a region and
 * re-encoding to MP3 only needs decode + slice + encode, so those browsers can
 * still create clips natively with no WebAssembly involved.
 */

/**
 * Minimal WebAssembly module that uses a SIMD instruction (i8x16.splat).
 * Taken from the well-known wasm-feature-detect SIMD probe.
 */
const SIMD_PROBE_MODULE = new Uint8Array([
  0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00, 0x01, 0x05, 0x01, 0x60, 0x00,
  0x01, 0x7b, 0x03, 0x02, 0x01, 0x00, 0x0a, 0x0a, 0x01, 0x08, 0x00, 0x41, 0x00,
  0xfd, 0x0f, 0xfd, 0x62, 0x0b,
]);

let simdSupported: boolean | null = null;

/**
 * Reports whether this environment can compile WebAssembly SIMD code.
 *
 * We compile the probe rather than only validating it, because some engines
 * validate SIMD successfully but still fail SIMD code generation, which is the
 * exact condition that produces "Wasm SIMD unsupported" at instantiate time.
 */
export const isWasmSimdSupported = (): boolean => {
  if (simdSupported !== null) return simdSupported;
  try {
    simdSupported = Boolean(new WebAssembly.Module(SIMD_PROBE_MODULE));
  } catch {
    simdSupported = false;
  }
  return simdSupported;
};

/** MP3 frames are encoded 1152 samples at a time. */
const MP3_FRAME_SIZE = 1152;

/** Matches the previous ffmpeg `-b:a 320k` setting. */
export const DEFAULT_CLIP_BITRATE_KBPS = 320;

type AudioContextConstructor = new () => AudioContext;

const getAudioContextCtor = (): AudioContextConstructor => {
  const win = window as unknown as {
    AudioContext?: AudioContextConstructor;
    webkitAudioContext?: AudioContextConstructor;
  };
  const Ctor = win.AudioContext || win.webkitAudioContext;
  if (!Ctor) {
    throw new Error('Web Audio API is not supported in this browser');
  }
  return Ctor;
};

/**
 * Decodes encoded audio bytes into an AudioBuffer.
 *
 * The supplied ArrayBuffer is copied because `decodeAudioData` detaches the
 * buffer it is given, and callers reuse the original audio for several clips.
 */
export const decodeAudio = async (
  audioData: ArrayBuffer,
): Promise<AudioBuffer> => {
  const Ctor = getAudioContextCtor();
  const audioCtx = new Ctor();
  try {
    return await audioCtx.decodeAudioData(audioData.slice(0));
  } finally {
    // Release the hardware/context; decoding does not need it afterwards.
    if (typeof audioCtx.close === 'function') {
      await audioCtx.close().catch(() => undefined);
    }
  }
};

/** Converts float PCM in the range [-1, 1] to signed 16-bit PCM. */
const toInt16 = (input: Float32Array): Int16Array => {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const sample = Math.max(-1, Math.min(1, input[i] ?? 0));
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output;
};

/**
 * Extracts the region between `start` and `end` (in seconds) from `audioBuffer`
 * and encodes it as an MP3 blob.
 *
 * Stereo sources stay stereo so music clips keep their original imaging.
 */
export const encodeRegionToMp3 = (
  audioBuffer: AudioBuffer,
  start: number,
  end: number,
  bitrateKbps: number = DEFAULT_CLIP_BITRATE_KBPS,
): Blob => {
  const {sampleRate, duration} = audioBuffer;

  const safeStart = Math.max(0, Math.min(start, duration));
  const safeEnd = Math.max(safeStart, Math.min(end, duration));

  const startSample = Math.floor(safeStart * sampleRate);
  const endSample = Math.floor(safeEnd * sampleRate);
  const frameCount = endSample - startSample;

  if (frameCount <= 0) {
    throw new Error('Selected clip region is empty');
  }

  // lamejs supports mono and stereo only.
  const channels = Math.min(audioBuffer.numberOfChannels, 2);
  const left = toInt16(
    audioBuffer.getChannelData(0).subarray(startSample, endSample),
  );
  const right =
    channels > 1
      ? toInt16(audioBuffer.getChannelData(1).subarray(startSample, endSample))
      : null;

  const encoder = new lamejs.Mp3Encoder(channels, sampleRate, bitrateKbps);
  const chunks: Int8Array[] = [];

  for (let offset = 0; offset < left.length; offset += MP3_FRAME_SIZE) {
    const leftChunk = left.subarray(offset, offset + MP3_FRAME_SIZE);
    const encoded = right
      ? encoder.encodeBuffer(
          leftChunk,
          right.subarray(offset, offset + MP3_FRAME_SIZE),
        )
      : encoder.encodeBuffer(leftChunk);
    if (encoded.length > 0) {
      chunks.push(encoded);
    }
  }

  const remaining = encoder.flush();
  if (remaining.length > 0) {
    chunks.push(remaining);
  }

  return new Blob(chunks as BlobPart[], {type: 'audio/mpeg'});
};
